import { DownloadIcon, PlusIcon, Save, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { MesAnoInput } from '@/components/form/input/date-mask-input'
import { MaskInputDin } from '@/components/ui/mask-input-din'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useToast } from '@/lib/hooks/use-toast'
import {
  useCreateTaxLancamento,
  useDeleteTaxLancamento,
  useExportPrevidenciarioTaxPlanilhas,
  useFetchTaxLancamentos,
  useUpdateTaxLancamento,
  type TaxLancamento
} from '../../services/useTaxLancamentos'

type ControleVerbasRow = {
  clientId: string
  creditoId?: string
  debitoId?: string
  creditoAnoCompetencia: string
  creditoMesCompetencia: string
  debitoAnoCompetencia: string
  debitoMesCompetencia: string
  debitoName: string
  valueOriginalCredito: number
  valueOriginalDebito: number
  valueSelic: number
  honorarios: number
  valueSaldo: number
  totalCompensado: number
  perDcomp: string
  situacaoPerDcomp: boolean
  situacaoPago: boolean
}

type PlanilhasPrevidenciarioProps = {
  idApuracaoCompetencia: string
  isEditable: boolean
}

const EMPTY_LANCAMENTOS: TaxLancamento[] = []

export default function PlanilhasPrevidenciario({
  idApuracaoCompetencia,
  isEditable
}: PlanilhasPrevidenciarioProps) {
  const { toast } = useToast()
  const { data: fetchedLancamentos } = useFetchTaxLancamentos(idApuracaoCompetencia)
  const { mutateAsync: createLancamento } = useCreateTaxLancamento()
  const { mutateAsync: updateLancamento } = useUpdateTaxLancamento()
  const { mutateAsync: deleteLancamento } = useDeleteTaxLancamento()
  const { mutate: exportPlanilhas, isPending: isExporting } = useExportPrevidenciarioTaxPlanilhas()
  const [rows, setRows] = useState<ControleVerbasRow[]>([])
  const canManageRows = isEditable && Boolean(idApuracaoCompetencia)
  const lancamentosData = fetchedLancamentos ?? EMPTY_LANCAMENTOS

  useEffect(() => {
    setRows(groupLancamentosIntoRows(lancamentosData))
  }, [lancamentosData])

  const totals = useMemo(
    () => ({
      credito: rows.reduce((sum, row) => sum + row.valueOriginalCredito, 0),
      debito: rows.reduce((sum, row) => sum + row.valueOriginalDebito, 0),
      valueSaldo: rows.reduce((sum, row) => sum + row.valueSaldo, 0),
      totalCompensado: rows.reduce((sum, row) => sum + row.totalCompensado, 0)
    }),
    [rows]
  )

  const handleAddRow = () => {
    setRows(prev => [
      ...prev,
      {
        clientId: createClientId(),
        creditoAnoCompetencia: '',
        creditoMesCompetencia: '',
        debitoAnoCompetencia: '',
        debitoMesCompetencia: '',
        debitoName: '',
        valueOriginalCredito: 0,
        valueOriginalDebito: 0,
        valueSelic: 0,
        honorarios: 0,
        valueSaldo: 0,
        totalCompensado: 0,
        perDcomp: '',
        situacaoPerDcomp: false,
        situacaoPago: false
      }
    ])
  }

  const handleChange = <K extends keyof ControleVerbasRow>(clientId: string, field: K, value: ControleVerbasRow[K]) => {
    setRows(prev => prev.map(row => (row.clientId === clientId ? { ...row, [field]: value } : row)))
  }

  const handleCompetenciaChange = (clientId: string, field: 'credito' | 'debito', value: string) => {
    const mes = value.slice(0, 2)
    const ano = value.slice(2)

    setRows(prev =>
      prev.map(row => {
        if (row.clientId !== clientId) return row

        return field === 'credito'
          ? { ...row, creditoMesCompetencia: mes, creditoAnoCompetencia: ano }
          : { ...row, debitoMesCompetencia: mes, debitoAnoCompetencia: ano }
      })
    )
  }

  const handleSaveRow = async (row: ControleVerbasRow) => {
    if (!idApuracaoCompetencia) {
      toast({ variant: 'destructive', description: 'Selecione uma apuração antes de salvar.' })
      return
    }

    const hasCredito = row.valueOriginalCredito > 0 || Boolean(row.creditoId)
    const hasDebito = row.valueOriginalDebito > 0 || Boolean(row.debitoId)

    if (!hasCredito && !hasDebito) {
      toast({ variant: 'destructive', description: 'Informe crédito ou débito antes de salvar.' })
      return
    }

    if (hasCredito && (!row.creditoMesCompetencia.trim() || !row.creditoAnoCompetencia.trim())) {
      toast({ variant: 'destructive', description: 'Informe mês e ano da competência do crédito.' })
      return
    }

    if (hasDebito && (!row.debitoMesCompetencia.trim() || !row.debitoAnoCompetencia.trim())) {
      toast({ variant: 'destructive', description: 'Informe mês e ano da competência do débito.' })
      return
    }

    if (hasDebito && !row.debitoName.trim()) {
      toast({ variant: 'destructive', description: 'Informe o nome do débito.' })
      return
    }

    try {
      const credito = hasCredito ? await saveCredito(row) : null
      const debito = hasDebito ? await saveDebito(row, !hasCredito) : null

      setRows(prev => prev.map(item => (item.clientId === row.clientId ? mergeSavedRow(row, credito, debito) : item)))
      toast({ description: 'Controle de crédito salvo com sucesso.' })
    } catch {
      toast({ variant: 'destructive', description: 'Houve um erro ao salvar o controle de crédito.' })
    }
  }

  const saveCredito = (row: ControleVerbasRow) => {
    const payload = {
      anoCompetencia: row.creditoAnoCompetencia.trim() || null,
      mesCompetencia: row.creditoMesCompetencia.trim() || null,
      valueOriginal: row.valueOriginalCredito,
      valueSelic: row.valueSelic,
      honorarios: row.honorarios,
      valueSaldo: row.valueSaldo,
      totalCompensado: row.totalCompensado,
      perDcomp: row.perDcomp || null,
      situacaoPerDcomp: row.situacaoPerDcomp,
      situacaoPago: row.situacaoPago
    }

    if (row.creditoId) {
      return updateLancamento({ id: row.creditoId, ...payload })
    }

    return createLancamento({
      id_apuracao_competencia: idApuracaoCompetencia,
      typeFiscalType: 'CREDITO',
      ...payload
    })
  }

  const saveDebito = (row: ControleVerbasRow, shouldCarrySharedFields: boolean) => {
    const sharedPayload = shouldCarrySharedFields
      ? {
          valueSelic: row.valueSelic,
          honorarios: row.honorarios,
          valueSaldo: row.valueSaldo,
          totalCompensado: row.totalCompensado,
          perDcomp: row.perDcomp || null,
          situacaoPerDcomp: row.situacaoPerDcomp,
          situacaoPago: row.situacaoPago
        }
      : {}
    const payload = {
      anoCompetencia: row.debitoAnoCompetencia.trim() || null,
      mesCompetencia: row.debitoMesCompetencia.trim() || null,
      name: row.debitoName.trim() || null,
      valueOriginal: row.valueOriginalDebito,
      ...sharedPayload
    }

    if (row.debitoId) {
      return updateLancamento({ id: row.debitoId, ...payload })
    }

    return createLancamento({
      id_apuracao_competencia: idApuracaoCompetencia,
      typeFiscalType: 'DEBITO',
      ...payload
    })
  }

  const handleDeleteRow = async (row: ControleVerbasRow) => {
    if (!row.creditoId && !row.debitoId) {
      setRows(prev => prev.filter(item => item.clientId !== row.clientId))
      return
    }

    try {
      if (row.creditoId) {
        await deleteLancamento({ id: row.creditoId, id_apuracao_competencia: idApuracaoCompetencia })
      }
      if (row.debitoId) {
        await deleteLancamento({ id: row.debitoId, id_apuracao_competencia: idApuracaoCompetencia })
      }

      setRows(prev => prev.filter(item => item.clientId !== row.clientId))
      toast({ description: 'Controle de crédito excluído com sucesso.' })
    } catch {
      toast({ variant: 'destructive', description: 'Houve um erro ao excluir o controle de crédito.' })
    }
  }

  const handleExport = () => {
    if (!idApuracaoCompetencia) {
      toast({ variant: 'destructive', description: 'Selecione uma apuração antes de exportar.' })
      return
    }
    exportPlanilhas(idApuracaoCompetencia)
  }

  return (
    <div className="bg-white rounded-lg shadow-lg mt-6 overflow-hidden mx-auto">
      <div className="p-6 border-b">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl text-foreground font-bold">Controle de Crédito</h2>
            <p className="text-sm text-muted-foreground">
              Crédito e débito na mesma linha, com dados comuns uma única vez
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} disabled={!idApuracaoCompetencia || isExporting}>
              <DownloadIcon className="h-4 w-4 mr-2" />
              Exportar planilhas
            </Button>
            <Button size="sm" onClick={handleAddRow} disabled={!canManageRows}>
              <PlusIcon className="h-4 w-4 mr-2" />
              Linha
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {!idApuracaoCompetencia && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Crie ou selecione uma apuração antes de criar o controle de crédito.
          </div>
        )}

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table className="w-full min-w-[1480px]">
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead>Competência Crédito</TableHead>
                  <TableHead>Crédito</TableHead>
                  <TableHead>Competência Débito</TableHead>
                  <TableHead>Nome Débito</TableHead>
                  <TableHead>Débito</TableHead>
                  <TableHead>Honorários</TableHead>
                  <TableHead>Selic</TableHead>
                  <TableHead>Saldo</TableHead>
                  <TableHead>Total Compensado</TableHead>
                  <TableHead>PER/DCOMP</TableHead>
                  <TableHead>PER/DCOMP OK</TableHead>
                  <TableHead>Pago</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.clientId}>
                    <MesAnoCell
                      value={`${row.creditoMesCompetencia || ''}${row.creditoAnoCompetencia ? '/' + row.creditoAnoCompetencia : ''}`}
                      disabled={!isEditable}
                      onChange={value => handleCompetenciaChange(row.clientId, 'credito', value)}
                    />
                    <CurrencyCell
                      value={row.valueOriginalCredito}
                      disabled={!isEditable}
                      onChange={value => handleChange(row.clientId, 'valueOriginalCredito', value)}
                    />
                    <MesAnoCell
                      value={`${row.debitoMesCompetencia || ''}${row.debitoAnoCompetencia ? '/' + row.debitoAnoCompetencia : ''}`}
                      disabled={!isEditable}
                      onChange={value => handleCompetenciaChange(row.clientId, 'debito', value)}
                    />
                    <TextCell
                      value={row.debitoName}
                      disabled={!isEditable}
                      placeholder="Nome"
                      onChange={value => handleChange(row.clientId, 'debitoName', value)}
                    />
                    <CurrencyCell
                      value={row.valueOriginalDebito}
                      disabled={!isEditable}
                      onChange={value => handleChange(row.clientId, 'valueOriginalDebito', value)}
                    />
                    <CurrencyCell
                      value={row.honorarios}
                      disabled={!isEditable}
                      onChange={value => handleChange(row.clientId, 'honorarios', value)}
                    />
                    <CurrencyCell
                      value={row.valueSelic}
                      disabled={!isEditable}
                      onChange={value => handleChange(row.clientId, 'valueSelic', value)}
                    />
                    <CurrencyCell
                      value={row.valueSaldo}
                      disabled={!isEditable}
                      onChange={value => handleChange(row.clientId, 'valueSaldo', value)}
                    />
                    <CurrencyCell
                      value={row.totalCompensado}
                      disabled={!isEditable}
                      onChange={value => handleChange(row.clientId, 'totalCompensado', value)}
                    />
                    <TableCell>
                      <input
                        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                        value={row.perDcomp}
                        onChange={event => handleChange(row.clientId, 'perDcomp', event.target.value)}
                        disabled={!isEditable}
                      />
                    </TableCell>
                    <BooleanCell
                      checked={row.situacaoPerDcomp}
                      disabled={!isEditable}
                      onChange={value => handleChange(row.clientId, 'situacaoPerDcomp', value)}
                    />
                    <BooleanCell
                      checked={row.situacaoPago}
                      disabled={!isEditable}
                      onChange={value => handleChange(row.clientId, 'situacaoPago', value)}
                    />
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleSaveRow(row)} disabled={!canManageRows}>
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteRow(row)} disabled={!canManageRows}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {rows.length === 0 && (
          <div className="text-center py-10 text-muted-foreground">Nenhum controle de crédito encontrado.</div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-4">
          <Summary label="Crédito" value={totals.credito} />
          <Summary label="Débito" value={totals.debito} />
          <Summary label="Saldo" value={totals.valueSaldo} />
          <Summary label="Total compensado" value={totals.totalCompensado} />
        </div>
      </div>
    </div>
  )
}

function CurrencyCell({
  value,
  disabled,
  onChange
}: {
  value: number
  disabled: boolean
  onChange: (value: number) => void
}) {
  return (
    <TableCell>
      <MaskInputDin className="w-full bg-input text-foreground" value={value} onChange={onChange} disabled={disabled} />
    </TableCell>
  )
}

function TextCell({
  value,
  disabled,
  placeholder,
  onChange
}: {
  value: string
  disabled: boolean
  placeholder?: string
  onChange: (value: string) => void
}) {
  return (
    <TableCell>
      <input
        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        value={value}
        placeholder={placeholder}
        onChange={event => onChange(event.target.value)}
        disabled={disabled}
      />
    </TableCell>
  )
}

function MesAnoCell({
  value,
  disabled,
  onChange
}: {
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  return (
    <TableCell>
      <MesAnoInput value={value} onChange={onChange} disabled={disabled} />
    </TableCell>
  )
}

function BooleanCell({
  checked,
  disabled,
  onChange
}: {
  checked: boolean
  disabled: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <TableCell>
      <Checkbox checked={checked} disabled={disabled} onCheckedChange={value => onChange(Boolean(value))} />
    </TableCell>
  )
}

function Summary({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border bg-background-secondary p-3">
      <p className="text-muted-foreground">{label}</p>
      <p className="font-semibold">{formatCurrency(value)}</p>
    </div>
  )
}

function groupLancamentosIntoRows(lancamentos: TaxLancamento[]): ControleVerbasRow[] {
  const creditos = lancamentos.filter(lancamento => lancamento.typeFiscalType === 'CREDITO')
  const debitos = lancamentos.filter(lancamento => lancamento.typeFiscalType === 'DEBITO')
  const rowCount = Math.max(creditos.length, debitos.length)

  return Array.from({ length: rowCount }, (_, index) => {
    const credito = creditos[index]
    const debito = debitos[index]
    const shared = credito ?? debito

    return {
      clientId: credito?.id ?? debito?.id ?? createClientId(),
      creditoId: credito?.id,
      debitoId: debito?.id,
      creditoAnoCompetencia: credito?.anoCompetencia ?? '',
      creditoMesCompetencia: credito?.mesCompetencia ?? '',
      debitoAnoCompetencia: debito?.anoCompetencia ?? '',
      debitoMesCompetencia: debito?.mesCompetencia ?? '',
      debitoName: debito?.name ?? '',
      valueOriginalCredito: Number(credito?.valueOriginal) || 0,
      valueOriginalDebito: Number(debito?.valueOriginal) || 0,
      valueSelic: Number(shared?.valueSelic) || 0,
      honorarios: Number(shared?.honorarios) || 0,
      valueSaldo: Number(shared?.valueSaldo) || 0,
      totalCompensado: Number(shared?.totalCompensado) || 0,
      perDcomp: shared?.perDcomp ?? '',
      situacaoPerDcomp: Boolean(shared?.situacaoPerDcomp),
      situacaoPago: Boolean(shared?.situacaoPago)
    }
  })
}

function createClientId() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function mergeSavedRow(
  current: ControleVerbasRow,
  credito: TaxLancamento | null,
  debito: TaxLancamento | null
): ControleVerbasRow {
  return {
    ...current,
    creditoId: credito?.id ?? current.creditoId,
    debitoId: debito?.id ?? current.debitoId,
    creditoAnoCompetencia: credito?.anoCompetencia ?? current.creditoAnoCompetencia,
    creditoMesCompetencia: credito?.mesCompetencia ?? current.creditoMesCompetencia,
    debitoAnoCompetencia: debito?.anoCompetencia ?? current.debitoAnoCompetencia,
    debitoMesCompetencia: debito?.mesCompetencia ?? current.debitoMesCompetencia,
    debitoName: debito?.name ?? current.debitoName,
    valueOriginalCredito: Number(credito?.valueOriginal ?? current.valueOriginalCredito) || 0,
    valueOriginalDebito: Number(debito?.valueOriginal ?? current.valueOriginalDebito) || 0,
    valueSelic: Number(credito?.valueSelic ?? debito?.valueSelic ?? current.valueSelic) || 0,
    honorarios: Number(credito?.honorarios ?? debito?.honorarios ?? current.honorarios) || 0,
    valueSaldo: Number(credito?.valueSaldo ?? debito?.valueSaldo ?? current.valueSaldo) || 0,
    totalCompensado: Number(credito?.totalCompensado ?? debito?.totalCompensado ?? current.totalCompensado) || 0,
    perDcomp: credito?.perDcomp ?? debito?.perDcomp ?? current.perDcomp,
    situacaoPerDcomp: credito?.situacaoPerDcomp ?? debito?.situacaoPerDcomp ?? current.situacaoPerDcomp,
    situacaoPago: credito?.situacaoPago ?? debito?.situacaoPago ?? current.situacaoPago
  }
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}
